import { ModalOpenButton } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { RestoreTemplateVersionModal } from "../RestoreTemplateVersionModal.js";
import { Button, ButtonProps } from "@mui/material";
import { ConfirmModal } from "../../../../commons/modals/ConfirmModal.js";
import { useApolloClient } from "@apollo/client";
import { useRestoreTemplateVersionMutation } from "../RestoreTemplateVersionModal.generated.js";
import { namedOperations } from "../../../../../clients/graphqlTypes.js";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";

interface Props {
  docId: string;
  handleComplete?: () => Promise<void> | void;
  buttonTitle?: string;
  buttonProps?: ButtonProps;
  restoreToSpecificVersionNumber?: number;
}

export const QuoteTemplateRestoreTemplateVersionButton = React.forwardRef<
  HTMLButtonElement,
  Props
>(
  (
    {
      buttonTitle,
      buttonProps,
      docId,
      handleComplete,
      restoreToSpecificVersionNumber,
    },
    ref
  ) => {
    const { t } = useTranslate("Templates");
    const client = useApolloClient();
    const [restoreTemplateVersion] = useRestoreTemplateVersionMutation({
      client,
      refetchQueries: [
        namedOperations.Query.TemplateQuoteRoutes,
        namedOperations.Query.TemplateQuoteEdit,
        namedOperations.Query.TemplateQuoteEditPreview,
        namedOperations.Query.TemplatesQuoteTemplateManageVersionsModal,
      ],
      awaitRefetchQueries: true,
    });

    if (restoreToSpecificVersionNumber) {
      return (
        <ModalOpenButton
          Modal={ConfirmModal}
          modalProps={{
            title: t("Restore template version"),
            text: t(
              "Any unpublished changes will be lost. Do you want to continue?"
            ),
            handleConfirm: async () => {
              await restoreTemplateVersion({
                variables: {
                  input: {
                    docId,
                    versionNumber: restoreToSpecificVersionNumber,
                  },
                },
              });
              await handleComplete?.();
            },
          }}
        >
          <Button
            ref={ref}
            variant="outlined"
            color="primary"
            startIcon={<SettingsBackupRestoreIcon />}
            {...buttonProps}
          >
            {buttonTitle ?? t("Restore a specific version")}
          </Button>
        </ModalOpenButton>
      );
    }

    return (
      <ModalOpenButton
        Modal={RestoreTemplateVersionModal}
        modalProps={{
          docId,
          handleComplete: async handleClose => {
            await handleComplete?.();
            handleClose();
          },
        }}
      >
        <Button
          ref={ref}
          variant="outlined"
          color="primary"
          startIcon={<SettingsBackupRestoreIcon />}
          {...buttonProps}
        >
          {buttonTitle ?? t("Restore a specific version")}
        </Button>
      </ModalOpenButton>
    );
  }
);
